import styled from 'styled-components'
import { darken, lighten } from 'polished'
import { ButtonHTMLAttributes, forwardRef } from 'react'

import { RowBetween } from '../Row'
import { ChevronDown } from 'react-feather'

// Custom ButtonProps to replace rebass ButtonProps
// Supports both transient ($prefix) and regular props for backwards compatibility
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: string
  // Support both formats for backwards compatibility
  width?: string
  $width?: string
  padding?: string
  $padding?: string
  borderRadius?: string
  $borderRadius?: string
  altDisabledStyle?: boolean
  $altDisabledStyle?: boolean
  mr?: string
  fontWeight?: number
  fontSize?: number
  // Polymorphic as prop for rendering as different elements (e.g., Link)
  as?: React.ElementType
  // Support Link props when used with as={Link}
  to?: string
}

// Internal transient props
interface BaseStyleProps {
  $padding?: string
  $width?: string
  $borderRadius?: string
  $altDisabledStyle?: boolean
}

const BaseStyled = styled.button<BaseStyleProps>`
  padding: ${({ $padding }) => ($padding ? $padding : '14px 24px')};
  width: ${({ $width }) => ($width ? $width : '100%')};
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.01em;
  text-align: center;
  border-radius: ${({ $borderRadius }) => $borderRadius ?? '16px'};
  outline: none;
  border: 1px solid transparent;
  color: ${({ theme }) => theme.text1};
  text-decoration: none;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: auto;
    transform: none;
  }

  > * {
    user-select: none;
  }
`

// Helper to convert props - extracts style props and passes through rest (including as, to)
const convertProps = (props: ButtonProps): BaseStyleProps & Omit<ButtonProps, keyof BaseStyleProps | 'width' | 'padding' | 'borderRadius' | 'altDisabledStyle' | 'mr' | 'fontWeight' | 'fontSize' | 'variant'> => {
  const {
    width,
    $width,
    padding,
    $padding,
    borderRadius,
    $borderRadius,
    altDisabledStyle,
    $altDisabledStyle,
    mr: _mr,
    fontWeight: _fontWeight,
    fontSize: _fontSize,
    variant: _variant,
    ...rest
  } = props
  return {
    $width: $width ?? width,
    $padding: $padding ?? padding,
    $borderRadius: $borderRadius ?? borderRadius,
    $altDisabledStyle: $altDisabledStyle ?? altDisabledStyle,
    ...rest,
  }
}

// Base component wrapper
const Base = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  return <BaseStyled ref={ref} {...convertProps(props)} />
})
Base.displayName = 'Base'

const ButtonPrimaryStyled = styled(BaseStyled)`
  background-color: ${({ theme }) => theme.green1};
  color: ${({ theme }) => theme.black1};

  /* Ensure all children inherit dark text color */
  & > *, & span, & div, & p {
    color: inherit !important;
  }

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.green1)};
    background-color: ${({ theme }) => darken(0.05, theme.green1)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.green1)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.green1)};
    background-color: ${({ theme }) => darken(0.1, theme.green1)};
  }
  &:disabled {
    background-color: ${({ theme, $altDisabledStyle }) => ($altDisabledStyle ? theme.green1 : theme.bg3)};
    color: ${({ theme, $altDisabledStyle }) => ($altDisabledStyle ? theme.black1 : theme.text3)};
    cursor: auto;
    box-shadow: none;
    border: 1px solid transparent;
    outline: none;
    opacity: ${({ $altDisabledStyle }) => ($altDisabledStyle ? '0.7' : '0.6')};
  }
`

export const ButtonPrimary = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  return <ButtonPrimaryStyled ref={ref} data-variant="primary" {...convertProps(props)} />
})
ButtonPrimary.displayName = 'ButtonPrimary'

export const ButtonLight = styled(BaseStyled)`
  background-color: ${({ theme }) => theme.bg2};
  color: ${({ theme }) => theme.text1};
  font-size: 16px;
  font-weight: 500;
  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme, disabled }) => !disabled && darken(0.03, theme.bg2)};
    background-color: ${({ theme, disabled }) => !disabled && darken(0.03, theme.bg2)};
  }
  &:hover {
    background-color: ${({ theme, disabled }) => !disabled && darken(0.03, theme.bg2)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme, disabled }) => !disabled && darken(0.05, theme.bg2)};
    background-color: ${({ theme, disabled }) => !disabled && darken(0.05, theme.bg2)};
  }
  :disabled {
    opacity: 0.4;
    :hover {
      cursor: auto;
      background-color: ${({ theme }) => theme.bg2};
      box-shadow: none;
      border: 1px solid transparent;
      outline: none;
    }
  }
`

export const ButtonGray = styled(BaseStyled)`
  background-color: ${({ theme }) => theme.bg3};
  color: ${({ theme }) => theme.text1};
  font-size: 16px;
  font-weight: 500;
  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme, disabled }) => !disabled && darken(0.05, theme.bg2)};
    background-color: ${({ theme, disabled }) => !disabled && darken(0.05, theme.bg2)};
  }
  &:hover {
    background-color: ${({ theme, disabled }) => !disabled && darken(0.05, theme.bg2)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme, disabled }) => !disabled && darken(0.1, theme.bg2)};
    background-color: ${({ theme, disabled }) => !disabled && darken(0.1, theme.bg2)};
  }
`

export const ButtonSecondary = styled(BaseStyled)`
  background-color: ${({ theme }) => theme.bg1};
  color: ${({ theme }) => theme.text1};
  font-size: 16px;
  border-radius: 8px;
  padding: ${({ $padding }) => ($padding ? $padding : '10px')};

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => theme.bg2};
    background-color: ${({ theme }) => theme.bg2};
  }
  &:hover {
    background-color: ${({ theme }) => theme.bg2};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => theme.bg2};
    background-color: ${({ theme }) => theme.bg2};
  }
  &:disabled {
    background-color: ${({ theme }) => theme.bg2};
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonPink = styled(BaseStyled)`
  background-color: ${({ theme }) => theme.red1};
  color: ${({ theme }) => theme.white1};

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.red1)};
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.red1)};
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
  &:disabled {
    background-color: ${({ theme }) => theme.red1};
    opacity: 50%;
    cursor: auto;
  }
`

const ButtonGreenStyled = styled(BaseStyled)`
  background-color: ${({ theme }) => theme.green1};
  color: ${({ theme }) => theme.black1};

  /* Ensure all children inherit dark text color */
  & > *, & span, & div, & p {
    color: inherit !important;
  }

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.green1)};
    background-color: ${({ theme }) => darken(0.05, theme.green1)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.green1)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.green1)};
    background-color: ${({ theme }) => darken(0.1, theme.green1)};
  }
  &:disabled {
    background-color: ${({ theme }) => theme.green1};
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonGreen = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  return <ButtonGreenStyled ref={ref} data-variant="primary" {...convertProps(props)} />
})
ButtonGreen.displayName = 'ButtonGreen'

export const ButtonOutlined = styled(BaseStyled)`
  border: 1px solid ${({ theme }) => theme.bg2};
  background-color: transparent;
  color: ${({ theme }) => theme.text1};

  &:focus {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.bg4};
  }
  &:hover {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.bg4};
  }
  &:active {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.bg4};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonEmpty = styled(BaseStyled)`
  background-color: transparent;
  color: ${({ theme }) => theme.text1};
  display: flex;
  justify-content: center;
  align-items: center;

  &:focus {
    background-color: ${({ theme }) => theme.advancedBG};
  }
  &:hover {
    background-color: ${({ theme }) => theme.advancedBG};
  }
  &:active {
    background-color: ${({ theme }) => theme.advancedBG};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

export const ButtonWhite = styled(BaseStyled)`
  border: 1px solid #edeef2;
  background-color: ${({ theme }) => theme.bg1};
  color: ${({ theme }) => theme.text1};

  &:focus {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.bg3)};
  }
  &:hover {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.bg3)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.bg3)};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

const ButtonConfirmedStyled = styled(BaseStyled)`
  background-color: ${({ theme }) => lighten(0.5, theme.green1)};
  color: ${({ theme }) => theme.black1};
  border: 1px solid ${({ theme }) => theme.green1};

  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`

const ButtonErrorStyled = styled(BaseStyled)`
  background-color: ${({ theme }) => theme.red1};
  border: 1px solid ${({ theme }) => theme.red1};
  color: ${({ theme }) => theme.white1};

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.red1)};
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.red1)};
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
    box-shadow: none;
    background-color: ${({ theme }) => theme.red1};
    border: 1px solid ${({ theme }) => theme.red1};
  }
`

export function ButtonConfirmed({
  confirmed,
  altDisabledStyle,
  $altDisabledStyle,
  width,
  $width,
  padding,
  $padding,
  ...rest
}: { confirmed?: boolean; altDisabledStyle?: boolean } & ButtonProps) {
  const styleProps = convertProps({ width, $width, padding, $padding, altDisabledStyle, $altDisabledStyle })
  if (confirmed) {
    return <ButtonConfirmedStyled {...styleProps} {...rest} />
  } else {
    return <ButtonPrimaryStyled data-variant="primary" {...styleProps} {...rest} />
  }
}

export function ButtonError({
  error,
  width,
  $width,
  padding,
  $padding,
  ...rest
}: { error?: boolean } & ButtonProps) {
  const styleProps = convertProps({ width, $width, padding, $padding })
  if (error) {
    return <ButtonErrorStyled {...styleProps} {...rest} />
  } else {
    return <ButtonPrimaryStyled data-variant="primary" {...styleProps} {...rest} />
  }
}

export function ButtonDropdown({
  disabled = false,
  children,
  width,
  $width,
  padding,
  $padding,
  ...rest
}: { disabled?: boolean } & ButtonProps) {
  const styleProps = convertProps({ width, $width, padding, $padding })
  return (
    <ButtonPrimaryStyled data-variant="primary" {...styleProps} {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
        <ChevronDown size={24} />
      </RowBetween>
    </ButtonPrimaryStyled>
  )
}

export function ButtonDropdownLight({
  disabled = false,
  children,
  width,
  $width,
  padding,
  $padding,
  ...rest
}: { disabled?: boolean } & ButtonProps) {
  const styleProps = convertProps({ width, $width, padding, $padding })
  return (
    <ButtonOutlined {...styleProps} {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
        <ChevronDown size={24} />
      </RowBetween>
    </ButtonOutlined>
  )
}

export function ButtonRadio({
  active,
  width,
  $width,
  padding,
  $padding,
  ...rest
}: { active?: boolean } & ButtonProps) {
  const styleProps = convertProps({ width, $width, padding, $padding })
  if (!active) {
    return <ButtonWhite {...styleProps} {...rest} />
  } else {
    return <ButtonPrimaryStyled data-variant="primary" {...styleProps} {...rest} />
  }
}
